import { useState, useRef, useCallback } from "react";
import type { TFunction } from "i18next";
import { extractErrorMessage } from "@/shared/utils";
import { baseApi, API_TAG_TYPES } from "@/shared/api";
import { useAppDispatch } from "@/shared/hooks";
import { signWithNCALayer, NCALayerError, arrayBufferToBase64 } from "../index";

type SigningPhase = "idle" | "previewing" | "signing" | "submitting";

interface PromptState {
  title: string;
  text: string;
  variant: "success" | "error";
}

interface UseOrderSigningOptions {
  previewFn: () => Promise<Blob>;
  signFn: (cmsSignature: string, fileBase64?: string) => Promise<unknown>;
  onSuccess: () => void;
  setPrompt: (prompt: PromptState) => void;
  t: TFunction;
  skipFileUpload?: boolean;
}

const POST_SIGNING_INVALIDATION_DELAY = 5000;
const POST_SIGNING_TAGS = API_TAG_TYPES.filter((t) => t !== "Auth");

export function useOrderSigning(options: UseOrderSigningOptions) {
  const optionsRef = useRef(options);
  optionsRef.current = options;
  const dispatch = useAppDispatch();

  const [phase, setPhase] = useState<SigningPhase>("idle");
  const phaseRef = useRef<SigningPhase>("idle");
  const isLoading = phase !== "idle";

  const handleSign = useCallback(async function handleSign() {
    if (phaseRef.current !== "idle") return;

    try {
      phaseRef.current = "previewing";
      setPhase("previewing");
      const blob = await optionsRef.current.previewFn();

      const arrayBuffer = await blob.arrayBuffer();
      const base64 = arrayBufferToBase64(arrayBuffer);

      phaseRef.current = "signing";
      setPhase("signing");
      const result = await signWithNCALayer(base64);

      phaseRef.current = "submitting";
      setPhase("submitting");
      if (optionsRef.current.skipFileUpload) {
        await optionsRef.current.signFn(result.signature);
      } else {
        await optionsRef.current.signFn(result.signature, base64);
      }

      phaseRef.current = "idle";
      setPhase("idle");
      setTimeout(() => {
        dispatch(baseApi.util.invalidateTags([...POST_SIGNING_TAGS]));
      }, POST_SIGNING_INVALIDATION_DELAY);
      optionsRef.current.onSuccess();
    } catch (err: unknown) {
      phaseRef.current = "idle";
      setPhase("idle");

      if (err instanceof NCALayerError) {
        if (err.code === "500") {
          return;
        }
        optionsRef.current.setPrompt({
          title: optionsRef.current.t("signing.errors.signingFailedTitle"),
          text: optionsRef.current.t(err.message),
          variant: "error",
        });
        return;
      }

      optionsRef.current.setPrompt({
        title: optionsRef.current.t("signing.errors.signingFailedTitle"),
        text: extractErrorMessage(err, "signing.errors.signingFailedText", optionsRef.current.t),
        variant: "error",
      });
    }
  }, [dispatch]);

  return { phase, isLoading, handleSign };
}
